import React from 'react';
import PropTypes from 'prop-types';
import { cloneObject, transformIntoDashCase } from '../../../common/utils';
import IconWithText from '../../layout/IconWithText';
import { allowedActionElementStyles, allowedButtonColors } from './_allowedPropTypes';
import ButtonElement from './_ButtonElement';
import LinkElement from './_LinkElement';
import PseudoElement from './_PseudoElement';

const handledActionElementTypes = {
  link: ['link', 'navLink'],
  button: ['button', 'submit'],
  pseudo: ['pseudo']
};

const ActionElementData = function (elementName, component, handledTypes) {
  this.elementName = elementName;
  this.handledTypes = handledTypes;
  this.component = component;
};

const actionElements = [
  new ActionElementData('link', LinkElement, handledActionElementTypes.link),
  new ActionElementData('button', ButtonElement, handledActionElementTypes.button),
  new ActionElementData('pseudo', PseudoElement, handledActionElementTypes.pseudo)
];

const ActionElementController = React.forwardRef((props, ref) => {
  const { 0: defaultAppearanceValue } = allowedActionElementStyles;

  const {
    type: passedType = 'button',
    appearance: passedAppearanceName = defaultAppearanceValue,
    disabled
  } = props;

  const actionElementType = ![
    ...handledActionElementTypes.button,
    ...handledActionElementTypes.pseudo
  ].includes(passedType) && disabled ? 'button' : passedType;

  const actionElementByType = actionElements.find(({ handledTypes }) => handledTypes.includes(actionElementType));

  if (!actionElementByType) {
    throw new Error(`Invalid action element type "${passedType}" - did you mix it up with "appearance"?`);
  }

  const {
    elementName,
    component: ElementComponent
  } = actionElementByType;

  const isActionElementLink = elementName === 'link';

  const {
    buttonColor = passedAppearanceName !== 'buttonWrap' && 'primary',
    className,
    iconName,
    letButtonShrink,
    iconAttributes = {},
    pullIconRight,
    children,
    sameTextAndIconColor = !isActionElementLink,
    isFullWidth,
    isAlternativeUnderline,
    hasSubmitButtonMargin
  } = props;

  if (passedAppearanceName === 'buttonLink' && !children) {
    console.error(
      'Warning: no content was passed to a button element that has "buttonLink" appearance - note that the '
      + 'appearance will not change on focusing or hovering the element since there is no text to underline',
      props
    );
  }

  const styleClassSuffix = passedAppearanceName !== defaultAppearanceValue
    ? transformIntoDashCase(passedAppearanceName)
    : elementName;

  const { elementName: elementType } = actionElements.find(({ iteratedElementName }) => {
    const familyName = new RegExp(`^${iteratedElementName}.*`);
    return familyName.test(passedAppearanceName);
  }) || { elementName };

  const isButtonAppearance = styleClassSuffix.split('-')[0] === 'button';
  const classNames = className ? className.split(' ') : [];
  const finalIconAttributes = cloneObject(iconAttributes, { fixedWidth: true });
  const elementProps = cloneObject(props, {}, [
    'appearance',
    'buttonColor',
    'iconName',
    'letButtonShrink',
    'iconAttributes',
    'pullIconRight',
    'children',
    'sameTextAndIconColor',
    'isFullWidth',
    'hasSubmitButtonMargin'
  ]);

  classNames.unshift(
    'action-element',
    `output-action-element--${elementType}`,
    `action-element-style--${styleClassSuffix}`
  );

  if (isButtonAppearance) {
    classNames.push(`button-color-${buttonColor}`);
  }

  if (isButtonAppearance && children && !letButtonShrink) {
    classNames.push('button-shrink-limited');
  }

  if (sameTextAndIconColor) {
    classNames.push('inherited-icon-color');
  }

  if (isFullWidth) {
    classNames.push('button-full-width');
  }

  if (isAlternativeUnderline) {
    classNames.push('alternative-underline');
  }

  if (hasSubmitButtonMargin) {
    classNames.push('button-submit-margin');
  }

  if (disabled) {
    classNames.push('action-element-disabled');
  }

  if (classNames.length > 0) {
    elementProps.className = [...new Set(classNames)].join(' ');
  }

  if (!handledActionElementTypes.button.includes(actionElementType)) {
    delete elementProps.disabled;
    delete elementProps.type;
  }

  else {
    elementProps.type = actionElementType;
  }

  return (
    <ElementComponent ref={ref} {...elementProps}>
      <IconWithText
        pullIconRight={pullIconRight}
        iconName={iconName}
        iconProps={finalIconAttributes}
        text={children}
      />
    </ElementComponent>
  );
});

ActionElementController.propTypes = {
  type: PropTypes.oneOf(['button', 'submit', 'link', 'navLink', 'pseudo']).isRequired,
  to: PropTypes.string, // only for type "link"
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  appearance: PropTypes.oneOf(allowedActionElementStyles),
  buttonColor: PropTypes.oneOf(allowedButtonColors),
  iconName: PropTypes.string,
  letButtonShrink: PropTypes.bool,
  sameTextAndIconColor: PropTypes.bool,
  hasSubmitButtonMargin: PropTypes.bool,
  isAlternativeUnderline: PropTypes.bool,
  isLinkExternal: PropTypes.bool, // only for type "link"
  isFullWidth: PropTypes.bool,
  iconAttributes: PropTypes.object,
  pullIconRight: PropTypes.bool,
  title(props, propName, componentName) {
    const {
      type,
      [propName]: value
    } = props;

    if (type !== 'pseudo' && !value) {
      return new Error(`Error: "${propName}" is required if action element type is not "pseudo" - in ${componentName}`);
    }
  }
};

export default ActionElementController;